<template>
  <div class="analysis-modal modal-overlay" @click="$emit('close')">
    <div class="modal-content large-modal" @click.stop>
      <div class="modal-header">
        <h2>
          <i class="fas fa-robot"></i>
          AI Transcript Analysis: {{ video.title }}
        </h2>
        <button @click="$emit('close')" class="modal-close">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <!-- Captions Generating Status -->
        <div v-if="captionsGenerating" class="generating-status">
          <div class="generating-icon">
            <i class="fas fa-magic"></i>
          </div>
          <h3>Vimeo is Generating Captions</h3>
          <p>The video transcript is being automatically generated. This usually takes 1-2 minutes.</p>
          
          <div class="progress-info">
            <div class="spinner"></div>
            <p><strong>Estimated time remaining:</strong> {{ Math.floor(retryInSeconds / 60) }} minutes</p>
          </div>

          <div class="action-buttons">
            <button @click="checkAgain" class="btn btn-primary btn-lg">
              <i class="fas fa-redo"></i>
              Check Again
            </button>
            <button @click="$emit('close')" class="btn btn-secondary">
              Close (I'll come back later)
            </button>
          </div>

          <div class="help-text">
            <i class="fas fa-info-circle"></i>
            <p>You can close this modal and come back in a minute to try again.</p>
          </div>
        </div>

        <!-- Step 1: Enter Vimeo Token -->
        <div v-else-if="!analysis" class="token-prompt">
          <div class="prompt-icon">
            <i class="fas fa-key"></i>
          </div>
          <h3>Vimeo Access Token</h3>
          <p v-if="hasOrgToken">Your organization has a Vimeo token saved. Click Analyze to use it, or enter a different token below.</p>
          <p v-else>To analyze the video transcript and fetch thumbnails, enter your Vimeo API access token.</p>
          
          <form @submit.prevent="runAnalysis">
            <div class="form-group">
              <label>Vimeo Access Token:</label>
              <input
                v-model="vimeoToken"
                type="password"
                placeholder="Enter your Vimeo access token..."
                class="form-input"
                autocomplete="off"
              />
              <small>
                <i class="fas fa-info-circle"></i>
                Get your token at <a href="https://developer.vimeo.com/" target="_blank">developer.vimeo.com</a>
              </small>
            </div>

            <div class="action-buttons">
              <button 
                type="submit"
                class="btn btn-primary btn-lg"
                :disabled="!vimeoToken || analyzing"
              >
                <i class="fas fa-magic"></i>
                {{ analyzing ? 'Analyzing...' : 'Analyze Video Transcript' }}
              </button>
            </div>
          </form>

          <div v-if="analyzing" class="analyzing-status">
            <div class="spinner"></div>
            <p>Fetching transcript from Vimeo...</p>
            <p>Extracting medical terms...</p>
            <p>Searching PubMed for related research...</p>
          </div>
        </div>

        <!-- Step 2: Show Analysis Results -->
        <div v-if="analysis && analysis.medical_terms && !generatingTimestamps" class="analysis-results">
          <!-- Summary Stats -->
          <div class="stats-grid">
            <div class="stat-card">
              <i class="fas fa-stethoscope"></i>
              <div class="stat-value">{{ analysis.medical_terms.total_terms_found || 0 }}</div>
              <div class="stat-label">Medical Terms</div>
            </div>
            <div class="stat-card">
              <i class="fas fa-file-alt"></i>
              <div class="stat-value">{{ analysis.transcript?.total_segments || 0 }}</div>
              <div class="stat-label">Transcript Segments</div>
            </div>
            <div class="stat-card">
              <i class="fas fa-flask"></i>
              <div class="stat-value">{{ analysis.search_queries?.length || 0 }}</div>
              <div class="stat-label">Search Queries</div>
            </div>
            <div class="stat-card">
              <i class="fas fa-book-medical"></i>
              <div class="stat-value">{{ totalArticles }}</div>
              <div class="stat-label">PubMed Articles</div>
            </div>
          </div>

          <!-- Medical Terms Preview -->
          <div class="terms-preview">
            <h3><i class="fas fa-tags"></i> Detected Medical Terms</h3>
            <div class="terms-grid">
              <div 
                v-for="term in analysis.medical_terms.terms.slice(0, 15)" 
                :key="term.term"
                class="term-badge"
                :class="`term-${term.category}`"
              >
                <span class="term-name">{{ term.term }}</span>
                <span class="term-meta">
                  <span class="term-count">{{ term.total_mentions }}x</span>
                  <span class="term-category">{{ term.category }}</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Auto-Generate Options -->
          <div class="auto-generate-section">
            <h3><i class="fas fa-magic"></i> Auto-Generate Timestamps</h3>
            <p>Automatically create timestamp markers from video content.</p>
            
            <!-- Generation Mode Selection -->
            <div class="generation-mode">
              <label class="mode-option">
                <input v-model="generationMode" type="radio" value="medical_terms" />
                <div class="mode-card">
                  <i class="fas fa-stethoscope"></i>
                  <strong>Medical Terms</strong>
                  <small>{{ analysis.medical_terms.total_terms_found || 0 }} terms available</small>
                </div>
              </label>
              
              <label class="mode-option">
                <input v-model="generationMode" type="radio" value="segments" />
                <div class="mode-card">
                  <i class="fas fa-film"></i>
                  <strong>Video Segments</strong>
                  <small>{{ analysis.transcript.total_segments || 0 }} segments available</small>
                </div>
              </label>
            </div>

            <!-- Options -->
            <div class="options-row">
              <label class="option-checkbox">
                <input v-model="fetchThumbnails" type="checkbox" />
                <i class="fas fa-image"></i>
                Fetch thumbnails from Vimeo
              </label>
              
              <div class="form-group-inline">
                <label>Max timestamps:</label>
                <input 
                  v-model.number="maxTimestamps" 
                  type="number" 
                  min="1" 
                  max="20"
                  class="form-input-small"
                />
              </div>

              <div v-if="generationMode === 'segments'" class="form-group-inline">
                <label>Interval (seconds):</label>
                <input 
                  v-model.number="segmentInterval" 
                  type="number" 
                  min="10" 
                  max="300"
                  step="10"
                  class="form-input-small"
                />
              </div>
            </div>

            <button 
              @click="autoGenerateTimestamps" 
              class="btn btn-success btn-lg"
              :disabled="fetchThumbnails && !vimeoToken && !hasOrgToken"
            >
              <i class="fas fa-bolt"></i>
              Auto-Generate {{ maxTimestamps }} Timestamps
              <span v-if="generationMode === 'medical_terms'">from Medical Terms</span>
              <span v-else>from Video Segments (every {{ segmentInterval }}s)</span>
            </button>
          </div>

          <!-- Preview What Will Be Created -->
          <div class="preview-section">
            <h4><i class="fas fa-eye"></i> Preview (Top {{ Math.min(maxTimestamps, analysis.medical_terms.terms.length) }} Timestamps)</h4>
            <div class="preview-list">
              <div 
                v-for="(term, index) in analysis.medical_terms.terms.slice(0, maxTimestamps)" 
                :key="index"
                class="preview-item"
              >
                <div class="preview-number">{{ index + 1 }}</div>
                <div class="preview-info">
                  <strong>{{ term.term }}</strong>
                  <span class="preview-time">
                    <i class="fas fa-clock"></i>
                    {{ formatTime(term.timestamps[0].start) }}
                  </span>
                  <span class="preview-mentions">{{ term.total_mentions }} mentions</span>
                </div>
                <div class="preview-thumbnail">
                  <i class="fas fa-image"></i>
                  {{ fetchThumbnails ? 'Will fetch from Vimeo' : 'No thumbnail' }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Generation Success -->
        <div v-if="timestampsGenerated" class="success-message">
          <div class="success-icon">
            <i class="fas fa-check-circle"></i>
          </div>
          <h3>Timestamps Generated Successfully!</h3>
          <p>Created {{ timestampsGenerated.created_count }} timestamps with medical term markers.</p>
          
          <div class="generated-list">
            <div 
              v-for="ts in timestampsGenerated.timestamps" 
              :key="ts.id"
              class="generated-item"
            >
              <i class="fas fa-check"></i>
              <strong>{{ ts.title }}</strong>
              <span>{{ formatTime(ts.time) }}</span>
              <span v-if="ts.thumbnail_url" class="has-thumbnail">
                <i class="fas fa-image"></i> Thumbnail
              </span>
            </div>
          </div>

          <button @click="$emit('close'); $emit('updated')" class="btn btn-primary btn-lg">
            <i class="fas fa-thumbs-up"></i>
            Done
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'

export default {
  name: 'TranscriptAnalysisModal',
  props: {
    video: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'updated'],
  setup(props) {
    const toast = useToast()
    const vimeoToken = ref('')
    const analyzing = ref(false)
    const analysis = ref(null)
    const fetchThumbnails = ref(true)
    const maxTimestamps = ref(10)
    const generatingTimestamps = ref(false)
    const timestampsGenerated = ref(null)
    const hasOrgToken = ref(false)
    const captionsGenerating = ref(false)
    const retryInSeconds = ref(0)
    const generationMode = ref('medical_terms')
    const segmentInterval = ref(30)

    // Check if organization has Vimeo token saved
    const checkOrgToken = async () => {
      try {
        const api = (await import('@/services/api.js')).default
        
        // First check if we can run analysis without token (backend will use org token)
        console.log('Checking if organization has Vimeo token...')
        
        analyzing.value = true
        
        try {
          const response = await api.post(`/lms/videos/${props.video.id}/analyze_transcript/`, {})
          
          // Check if this is a "generating captions" status (202) or actual analysis
          if (response.status === 202 || response.data.status === 'generating_captions') {
            console.log('⏳ Vimeo is generating captions...')
            captionsGenerating.value = true
            retryInSeconds.value = response.data.retry_in_seconds || 120
            const message = response.data.error || 'Vimeo is generating captions...'
            toast.info(message, { timeout: 8000 })
            hasOrgToken.value = true
            analyzing.value = false
            return
          }
          
          // Success! Actual analysis complete
          console.log('✅ Analysis complete:', response.data)
          hasOrgToken.value = true
          analysis.value = response.data
          analyzing.value = false
          toast.success(`Analysis complete! Found ${response.data.medical_terms.total_terms_found} medical terms.`)
          
        } catch (apiErr) {
          analyzing.value = false
          
          // Check if captions are being generated (202 status)
          if (apiErr.response?.status === 202) {
            console.log('⏳ Vimeo is generating captions...')
            captionsGenerating.value = true
            retryInSeconds.value = apiErr.response.data.retry_in_seconds || 120
            const message = apiErr.response.data.error || 'Vimeo is generating captions...'
            toast.info(message, { timeout: 8000 })
            hasOrgToken.value = true  // Org has token, just waiting for captions
            return
          }
          
          // Check if error is about missing token
          if (apiErr.response?.status === 400 && apiErr.response?.data?.error?.includes('token')) {
            console.log('❌ No org token available, showing token form')
            hasOrgToken.value = false
            return
          }
          
          // Other errors - show token form as fallback
          console.log('⚠️ Analysis failed:', apiErr.response?.data || apiErr.message)
          hasOrgToken.value = false
        }
        
      } catch (err) {
        analyzing.value = false
        console.error('Unexpected error in checkOrgToken:', err)
        hasOrgToken.value = false
      }
    }

    const totalArticles = computed(() => {
      if (!analysis.value || !analysis.value.pubmed_results) return 0
      return analysis.value.pubmed_results.reduce((sum, result) => {
        const articles = result.articles?.result?.uids || []
        return sum + articles.length
      }, 0)
    })

    const runAnalysis = async (useOrgToken = false) => {
      // Only require manual token if org doesn't have one
      if (!useOrgToken && !vimeoToken.value && !hasOrgToken.value) {
        toast.error('Please enter your Vimeo access token')
        return
      }

      analyzing.value = true

      try {
        const api = (await import('@/services/api.js')).default
        
        // Send token only if manually entered, otherwise backend uses org token
        const payload = vimeoToken.value ? { vimeo_access_token: vimeoToken.value } : {}
        
        const response = await api.post(`/lms/videos/${props.video.id}/analyze_transcript/`, payload)

        analysis.value = response.data
        toast.success(`Analysis complete! Found ${response.data.medical_terms.total_terms_found} medical terms.`)
      } catch (err) {
        console.error('Error analyzing video:', err)
        const errorMsg = err.response?.data?.error || 'Failed to analyze video transcript'
        toast.error(errorMsg)
      } finally {
        analyzing.value = false
      }
    }

    const autoGenerateTimestamps = async () => {
      console.log('Auto-generate clicked!', {
        fetchThumbnails: fetchThumbnails.value,
        hasOrgToken: hasOrgToken.value,
        maxTimestamps: maxTimestamps.value
      })
      
      // Token required only if fetching thumbnails
      if (fetchThumbnails.value && !vimeoToken.value && !hasOrgToken.value) {
        toast.error('Vimeo token required to fetch thumbnails')
        return
      }

      generatingTimestamps.value = true

      try {
        const api = (await import('@/services/api.js')).default
        
        // Send token only if manually entered, otherwise backend uses org token
        const payload = {
          fetch_thumbnails: fetchThumbnails.value,
          max_timestamps: maxTimestamps.value,
          generation_mode: generationMode.value,
          segment_interval: segmentInterval.value
        }
        if (vimeoToken.value) {
          payload.vimeo_access_token = vimeoToken.value
        }
        
        console.log('Generating timestamps with payload:', payload)
        const response = await api.post(`/lms/videos/${props.video.id}/auto_generate_timestamps/`, payload)

        timestampsGenerated.value = response.data
        toast.success(`Created ${response.data.created_count} timestamps!`)
      } catch (err) {
        console.error('Error generating timestamps:', err)
        toast.error('Failed to auto-generate timestamps')
      } finally {
        generatingTimestamps.value = false
      }
    }

    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    const checkAgain = async () => {
      captionsGenerating.value = false
      await checkOrgToken()
    }

    // Check for org token on mount
    onMounted(() => {
      console.log('TranscriptAnalysisModal mounted for video:', props.video.id)
      checkOrgToken()
    })

    return {
      vimeoToken,
      analyzing,
      analysis,
      hasOrgToken,
      captionsGenerating,
      retryInSeconds,
      generationMode,
      segmentInterval,
      fetchThumbnails,
      maxTimestamps,
      generatingTimestamps,
      timestampsGenerated,
      totalArticles,
      runAnalysis,
      autoGenerateTimestamps,
      checkAgain,
      formatTime
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: #1a1a1a;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  width: 90vw;
}

.modal-header {
  padding: var(--spacing-lg) var(--spacing-xl);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
}

.modal-header h2 {
  margin: 0;
  color: white;
  font-size: 1.5rem;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: var(--spacing-sm);
  opacity: 0.7;
}

.modal-close:hover {
  opacity: 1;
}

.modal-body {
  padding: var(--spacing-xl);
}

.large-modal {
  width: 90vw;
}

/* Generating Status */
.generating-status {
  text-align: center;
  padding: var(--spacing-xxl);
}

.generating-icon {
  font-size: 5rem;
  color: #f59e0b;
  margin-bottom: var(--spacing-lg);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

.generating-status h3 {
  color: white;
  margin-bottom: var(--spacing-md);
}

.generating-status p {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: var(--spacing-lg);
}

.progress-info {
  margin: var(--spacing-xl) 0;
  padding: var(--spacing-lg);
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 8px;
}

.progress-info .spinner {
  margin: 0 auto var(--spacing-md) auto;
}

.progress-info p {
  color: #f59e0b;
  font-size: 1.1rem;
}

.help-text {
  margin-top: var(--spacing-xl);
  padding: var(--spacing-md);
  background: rgba(59, 130, 246, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
}

.help-text i {
  color: #3b82f6;
  margin-right: var(--spacing-sm);
}

/* Token Prompt */
.token-prompt {
  text-align: center;
  padding: var(--spacing-xl);
}

.prompt-icon {
  font-size: 4rem;
  color: var(--primary-color);
  margin-bottom: var(--spacing-lg);
}

.token-prompt h3 {
  margin-bottom: var(--spacing-md);
}

.token-prompt p {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: var(--spacing-xl);
}

.action-buttons {
  margin-top: var(--spacing-xl);
}

.analyzing-status {
  margin-top: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: rgba(59, 130, 246, 0.1);
  border-radius: 8px;
}

.analyzing-status .spinner {
  margin: 0 auto var(--spacing-md) auto;
}

.analyzing-status p {
  color: rgba(255, 255, 255, 0.8);
  margin: var(--spacing-sm) 0;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-xl);
}

.stat-card {
  text-align: center;
  padding: var(--spacing-lg);
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 8px;
}

.stat-card i {
  font-size: 2rem;
  color: #10b981;
  margin-bottom: var(--spacing-sm);
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
  color: white;
  margin-bottom: var(--spacing-xs);
}

.stat-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.6);
}

/* Terms Preview */
.terms-preview {
  margin-bottom: var(--spacing-xl);
}

.terms-preview h3 {
  margin-bottom: var(--spacing-md);
}

.terms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--spacing-sm);
}

.term-badge {
  display: flex;
  flex-direction: column;
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
}

.term-badge.term-procedure {
  border-left: 3px solid #ef4444;
}

.term-badge.term-condition {
  border-left: 3px solid #f59e0b;
}

.term-badge.term-anatomy {
  border-left: 3px solid #3b82f6;
}

.term-badge.term-diagnostic {
  border-left: 3px solid #8b5cf6;
}

.term-name {
  font-weight: 500;
  color: white;
  margin-bottom: var(--spacing-xs);
}

.term-meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
}

.term-count {
  color: #10b981;
}

.term-category {
  text-transform: capitalize;
}

/* Auto-Generate Section */
.auto-generate-section {
  margin: var(--spacing-xl) 0;
  padding: var(--spacing-xl);
  background: rgba(16, 185, 129, 0.1);
  border: 2px solid rgba(16, 185, 129, 0.3);
  border-radius: 8px;
}

.auto-generate-section h3 {
  margin-bottom: var(--spacing-md);
  color: #10b981;
}

/* Generation Mode */
.generation-mode {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
  margin: var(--spacing-lg) 0;
}

.mode-option {
  cursor: pointer;
}

.mode-option input[type="radio"] {
  display: none;
}

.mode-card {
  padding: var(--spacing-lg);
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  text-align: center;
  transition: all 0.2s ease;
  cursor: pointer;
}

.mode-option input:checked + .mode-card {
  background: rgba(16, 185, 129, 0.2);
  border-color: #10b981;
}

.mode-card:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.mode-card i {
  font-size: 2rem;
  color: #10b981;
  display: block;
  margin-bottom: var(--spacing-sm);
}

.mode-card strong {
  display: block;
  color: white;
  margin-bottom: var(--spacing-xs);
}

.mode-card small {
  display: block;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
}

.options-row {
  display: flex;
  gap: var(--spacing-lg);
  align-items: center;
  margin: var(--spacing-lg) 0;
}

.option-checkbox {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  color: white;
}

.option-checkbox input {
  cursor: pointer;
}

.form-group-inline {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.form-input-small {
  width: 80px;
  padding: var(--spacing-sm);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: white;
}

/* Preview Section */
.preview-section {
  margin-top: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
}

.preview-section h4 {
  margin-bottom: var(--spacing-md);
  color: rgba(255, 255, 255, 0.9);
}

.preview-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.preview-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}

.preview-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: rgba(16, 185, 129, 0.2);
  color: #10b981;
  border-radius: 50%;
  font-weight: bold;
}

.preview-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.preview-info strong {
  color: white;
}

.preview-time,
.preview-mentions {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
}

.preview-thumbnail {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
}

/* Success Message */
.success-message {
  text-align: center;
  padding: var(--spacing-xxl);
}

.success-icon {
  font-size: 5rem;
  color: #10b981;
  margin-bottom: var(--spacing-lg);
}

.success-message h3 {
  color: white;
  margin-bottom: var(--spacing-md);
}

.generated-list {
  margin: var(--spacing-xl) 0;
  max-height: 300px;
  overflow-y: auto;
}

.generated-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  margin-bottom: var(--spacing-sm);
}

.generated-item i {
  color: #10b981;
}

.has-thumbnail {
  color: #10b981;
}

.btn-lg {
  padding: var(--spacing-md) var(--spacing-xl);
  font-size: 1.1rem;
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-success:hover {
  background: #059669;
}
</style>

